// src/hooks/useAutoRefresh.js
import { useEffect, useRef, useCallback } from 'react';
import { useDashboard } from '../contexts/DashboardContext';

const useAutoRefresh = (fetchFunction, dependencies = [], options = {}) => {
  const {
    refreshKey,
    refreshDashboard,
    addPendingUpdate,
    pendingUpdates
  } = useDashboard();
  
  const {
    interval = 30000, // 30 secondes par défaut
    enabled = true,
    onSuccess,
    onError,
    showNotifications = true
  } = options;

  const isMounted = useRef(true);
  const fetchRef = useRef(fetchFunction);
  const timeoutRef = useRef(null);

  // Mettre à jour la référence de la fonction fetch
  useEffect(() => {
    fetchRef.current = fetchFunction;
  }, [fetchFunction]);

  // Fonction pour exécuter le fetch avec gestion d'erreur
  const executeFetch = useCallback(async (source = 'auto') => {
    if (!isMounted.current) return;

    try {
      const result = await fetchRef.current();
      
      if (onSuccess) onSuccess(result);
      
      // Notifier le contexte du succès
      if (showNotifications && source === 'websocket') {
        // La notification est déjà gérée par le contexte
      }
      
      return result;
    } catch (error) {
      console.error(`❌ Erreur lors du fetch (${source}):`, error);
      if (onError) onError(error);
    }
  }, [onSuccess, onError, showNotifications]);

  // Rafraîchissement automatique basé sur refreshKey
  useEffect(() => {
    if (enabled && refreshKey > 0) {
      executeFetch(refreshDashboard.updateSource || 'context');
    }
  }, [refreshKey, enabled, executeFetch, refreshDashboard.updateSource]);

  // Rafraîchissement périodique
  useEffect(() => {
    if (!enabled || interval <= 0) return;

    const intervalId = setInterval(() => {
      executeFetch('periodic');
    }, interval);

    return () => clearInterval(intervalId);
  }, [interval, enabled, executeFetch]);

  // Gérer les mises à jour en attente
  useEffect(() => {
    if (pendingUpdates.length > 0) {
      // Traiter les mises à jour en attente
      const latestUpdate = pendingUpdates[pendingUpdates.length - 1];
      
      // Si c'est une mise à jour critique, rafraîchir immédiatement
      if (latestUpdate.priority === 'high') {
        executeFetch('critical');
      }
    }
  }, [pendingUpdates, executeFetch]);

  // Nettoyage
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    refresh: () => executeFetch('manual'),
    isRefreshing: false // À améliorer avec un état de chargement si nécessaire
  };
};

export default useAutoRefresh;